import { useGetPermissionsQuery } from '@consolidocs-ui/data-access/UserApi';
import { ErrorTypeWithDetails } from '@consolidocs-ui/types';
import { DepartmentDetails, UserPermissions } from '@consolidocs-ui/types';
import { useMemo } from 'react';
import { useAuth } from 'react-oidc-context';

export const SELECTED_USER_ROLE_KEY = 'selectedUserRole';
export const USER_ROLES_DETAILS = 'userRolesDetails';
export const ORGANIZATION_DETAILS = 'organizationDetails';

const useUserPermissions = (): {
  isFetching: boolean;
  rolePermissions: string[];
  roles?: UserPermissions['roles'];
  selectedRoleKey?: string;
  departmentId?: string;
  error?: ErrorTypeWithDetails;
  roleFile?: string;
  isCompanyAdmin?: boolean;
  isDepartmentAdmin?: boolean;
  isIndividualUser?: boolean;
  isTrialUser?: boolean;
  isPOCUser?: boolean;
  usePersonalDashboardConfig?: boolean;
  useSummaryGeneration?: boolean;
} => {
  const {
    data: userPermissionRes,
    isFetching,
    error,
  } = useGetPermissionsQuery('');
  const userRoles = userPermissionRes?.roles;
  const { user } = useAuth();
  const errorWithType = error as ErrorTypeWithDetails;
  const groups = user?.profile?.['cognito:groups'];
  const isAdmin = Array.isArray(groups) && groups.includes('Admin');
  const defaultFirst = {} as Record<string, DepartmentDetails>;

  try {
    localStorage.setItem(
      USER_ROLES_DETAILS,
      JSON.stringify(
        userRoles?.reduce((first, current) => {
          first[`${current.role_id}/${current.department_id}`] =
            current.department_details;
          return first;
        }, defaultFirst)
      )
    );

    localStorage.setItem(
      ORGANIZATION_DETAILS,
      JSON.stringify(userPermissionRes?.organization || {})
    );
  } catch (error) {
    console.error('Error saving user permissions to localStorage:', error);
  }

  const selectedRolePermisssionsAndKey = useMemo(() => {
    if (!userRoles || userRoles.length === 0) {
      return {
        rolePermissions: [],
        isCompanyAdmin: isAdmin,
        roleFile: '',
      };
    }

    let roleKey = localStorage.getItem(SELECTED_USER_ROLE_KEY);
    let selected = userRoles.find(
      (r) => `${r.role_id}/${r.department_id}` === roleKey
    );

    if (!selected) {
      selected = userRoles[0];
      roleKey = `${selected.role_id}/${selected.department_id}`;
      localStorage.setItem(SELECTED_USER_ROLE_KEY, roleKey);
    }

    return {
      selectedRoleKey: roleKey || undefined,
      rolePermissions: selected?.permissions,
      departmentId: selected?.department_id,
      roleFile: (selected?.permissions ?? [])[0] ?? '',
      isCompanyAdmin: !!isAdmin || selected?.permissions[0] === 'company-admin',
      isDepartmentAdmin:
        (selected?.permissions ?? [])[0] === 'department-admin',
      isIndividualUser: (selected?.permissions ?? [])[0] === 'individual-user',
      isTrialUser: (selected?.permissions ?? [])[0] === 'trial-user',
      isPOCUser: (selected?.permissions ?? [])[0] === 'poc-user',
    };
  }, [userRoles, user, isAdmin]);

  return {
    isFetching,
    roles: userRoles,
    ...selectedRolePermisssionsAndKey,
    error: errorWithType,
    usePersonalDashboardConfig:
      userPermissionRes?.personalized_dashboard ?? false,
    useSummaryGeneration: userPermissionRes?.show_summary ?? false,
  };
};

export default useUserPermissions;
